import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Sparkles,
  ShieldCheck,
  Award,
  HeartHandshake,
  Stethoscope,
  Languages,
  ArrowRight,
  CalendarCheck,
  MessageCircle,
  Crown,
  BadgeCheck,
  Cpu,
  Lock,
  HandHeart,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import useSeo from '../hooks/useSeo';
import TeamSection from '../components/sections/TeamSection';

const API_URL =
  import.meta.env.VITE_API_URL ||
  (typeof window !== 'undefined' ? window.location.origin : '');

const FALLBACK_CONTENT = {
  title: 'About Crystal Dental',
  subtitle: 'Premium dental care in Alanya since 2010',
  body: '',
  mission: '',
  vision: '',
  image_url: '',
  stat1_label: 'Years of experience',
  stat1_value: '15+',
  stat2_label: 'International patients',
  stat2_value: '5000+',
  stat3_label: 'Specialist dentists',
  stat3_value: '12',
  stat4_label: 'Patient satisfaction',
  stat4_value: '98%',
};

const VALUE_ICONS = [Crown, HeartHandshake, BadgeCheck, Cpu, Lock, HandHeart];

const CERTIFICATIONS = [
  { src: '/images/certifications/heart-of-health.svg', alt: 'Heart of Health' },
];

const About = () => {
  const { t, translations, currentLanguage } = useLanguage();
  const [content, setContent] = useState(FALLBACK_CONTENT);
  const [loading, setLoading] = useState(true);
  useSeo('about', currentLanguage);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/about`, {
          params: { lang: currentLanguage },
        });
        if (cancelled) return;
        setContent({ ...FALLBACK_CONTENT, ...(res.data?.data || {}) });
      } catch (err) {
        console.error('Failed to load about content:', err);
        if (!cancelled) setContent(FALLBACK_CONTENT);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [currentLanguage]);

  const stats = [1, 2, 3, 4]
    .map((n) => ({
      label: content?.[`stat${n}_label`],
      value: content?.[`stat${n}_value`],
    }))
    .filter((s) => s.value || s.label);

  const valuesItems =
    Array.isArray(translations?.aboutPage?.values?.items) &&
    translations.aboutPage.values.items.length
      ? translations.aboutPage.values.items
      : [
          { title: 'Premium quality', desc: 'Globally trusted brands and digital workflows.' },
          { title: 'Patient-first care', desc: 'Pain-free protocols and dedicated coordinators.' },
          { title: 'Transparent pricing', desc: 'Detailed, written quotes — no hidden fees.' },
          { title: 'Modern technology', desc: '3D imaging, intraoral scanning, CAD/CAM zirconia.' },
          { title: 'Lasting warranty', desc: 'Lifetime implant warranty, up to 10 years on crowns.' },
          { title: 'Hospitality mindset', desc: 'VIP transfers, beachfront stays, full aftercare.' },
        ];

  const storyBody =
    content?.body ||
    t(
      'aboutPage.story.fallbackBody',
      "Crystal Dental Clinic offers internationally certified implant, cosmetic and aesthetic dentistry services in the heart of Alanya. From the moment you land at the airport, our multilingual coordination team and modern, sterile clinic environment make every step of your treatment safe and comfortable.\n\nWe believe high-quality dental care shouldn't come with hidden fees or compromises. That's why every treatment plan is detailed, transparent and tailored to your goals."
    );

  const missionText =
    content?.mission || t('aboutPage.mission.fallback', '');
  const visionText =
    content?.vision || t('aboutPage.vision.fallback', '');

  const heroImage =
    content?.image_url ||
    'https://images.unsplash.com/photo-1629909613654-28e377c37b09?auto=format&fit=crop&w=1400&q=80';

  return (
    <div className="about-page">
      {/* Hero */}
      <section className="about-hero">
        <div className="container-premium">
          <div className="about-hero-inner">
            <span className="section-eyebrow light">
              {t('aboutPage.hero.eyebrow', 'About Crystal Dental')}
            </span>
            <h1 className="about-hero-title">
              {t('aboutPage.hero.title', 'Premium dental care, with a personal touch')}
            </h1>
            <p className="about-hero-sub">
              {t(
                'aboutPage.hero.subtitle',
                'An internationally trained team, certified facilities and a multilingual coordination service — built around your comfort, in the heart of Alanya.'
              )}
            </p>
            <div className="about-hero-badges">
              <span className="about-hero-badge">
                <ShieldCheck size={14} />
                {t('aboutPage.hero.badges.iso', 'ISO 9001 Certified')}
              </span>
              <span className="about-hero-badge">
                <Sparkles size={14} />
                {t('aboutPage.hero.badges.since', 'Caring since 2010')}
              </span>
              <span className="about-hero-badge">
                <Award size={14} />
                {t('aboutPage.hero.badges.warranty', 'Lifetime warranty')}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Story + image */}
      <section className="section-padding section-tone-b">
        <div className="container-premium">
          <div className="about-story-grid">
            <div className="about-story-text">
              <span className="section-eyebrow">
                {t('aboutPage.story.eyebrow', 'Our story')}
              </span>
              <h2 className="section-title left">
                {content?.title ||
                  t(
                    'aboutPage.story.title',
                    'Built on trust, perfected over the years'
                  )}
              </h2>
              {content?.subtitle ? (
                <p className="about-story-lead">{content.subtitle}</p>
              ) : null}
              {loading ? (
                <p className="muted">Loading…</p>
              ) : (
                <div className="about-story-body">
                  {storyBody.split('\n').map((para, idx) =>
                    para.trim() ? (
                      <p key={idx}>{para}</p>
                    ) : null
                  )}
                </div>
              )}
              <div className="about-story-cta">
                <Link to="/appointment" className="btn-crystal">
                  <CalendarCheck size={16} />
                  {t('aboutPage.cta.primary', 'Book Appointment')}
                </Link>
                <Link to="/contact" className="btn-crystal-outline">
                  {t('aboutPage.cta.secondary', 'Contact us')}
                  <ArrowRight size={16} />
                </Link>
              </div>
            </div>
            <div className="about-story-visual">
              <img
                src={heroImage}
                alt={content?.title || 'Crystal Dental Clinic'}
                loading="lazy"
              />
              <div className="about-story-floater">
                <Stethoscope size={18} />
                <div>
                  <strong>{t('aboutPage.hero.badges.iso', 'ISO 9001 Certified')}</strong>
                  <span>{t('aboutPage.certifications.subtitle', 'Independently audited.')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      {(missionText || visionText) && (
        <section className="section-padding section-tone-a">
          <div className="container-premium">
            <div className="about-mv-grid">
              {missionText ? (
                <article className="about-mv-card">
                  <div className="about-mv-icon mission">
                    <Sparkles size={20} />
                  </div>
                  <h3>{t('aboutPage.mission.label', 'Our Mission')}</h3>
                  <p>{missionText}</p>
                </article>
              ) : null}
              {visionText ? (
                <article className="about-mv-card">
                  <div className="about-mv-icon vision">
                    <Award size={20} />
                  </div>
                  <h3>{t('aboutPage.vision.label', 'Our Vision')}</h3>
                  <p>{visionText}</p>
                </article>
              ) : null}
            </div>
          </div>
        </section>
      )}

      {/* Stats */}
      {stats.length > 0 && (
        <section className="section-padding section-tone-b">
          <div className="container-premium">
            <div className="section-head">
              <span className="section-eyebrow">
                {t('aboutPage.stats.title', 'By the numbers')}
              </span>
              <h2 className="section-title">
                {t('aboutPage.stats.subtitle', 'More than a decade of premium care')}
              </h2>
            </div>
            <div className="about-stats-grid">
              {stats.map((s, idx) => (
                <article key={idx} className="about-stat-card">
                  <p className="about-stat-value">{s.value}</p>
                  <p className="about-stat-label">{s.label}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Values */}
      <section className="section-padding section-tone-c">
        <div className="container-premium">
          <div className="section-head">
            <span className="section-eyebrow">
              {t('aboutPage.values.eyebrow', 'What we stand for')}
            </span>
            <h2 className="section-title">
              {t('aboutPage.values.title', 'The values that guide our team')}
            </h2>
            <p className="section-sub">
              {t(
                'aboutPage.values.subtitle',
                'Six principles that shape every consultation, treatment and follow-up.'
              )}
            </p>
          </div>
          <div className="about-values-grid">
            {valuesItems.map((v, idx) => {
              const Icon = VALUE_ICONS[idx % VALUE_ICONS.length];
              return (
                <article key={idx} className="about-value-card">
                  <div className="about-value-icon">
                    <Icon size={22} />
                  </div>
                  <h3>{v.title}</h3>
                  <p>{v.desc}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="section-padding section-tone-b">
        <div className="container-premium">
          <div className="section-head">
            <span className="section-eyebrow">
              {t('aboutPage.certifications.title', 'Certifications & accreditations')}
            </span>
            <h2 className="section-title">
              {t('aboutPage.certifications.subtitle', 'Independently audited, internationally recognised')}
            </h2>
          </div>
          <div className="about-certs">
            {CERTIFICATIONS.map((c) => (
              <div key={c.src} className="about-cert-card">
                <img src={c.src} alt={c.alt} loading="lazy" />
                <span>{c.alt}</span>
              </div>
            ))}
            <div className="about-cert-card placeholder">
              <ShieldCheck size={28} />
              <span>ISO 9001</span>
            </div>
            <div className="about-cert-card placeholder">
              <Languages size={28} />
              <span>EN · TR · RU · NO · DE</span>
            </div>
            <div className="about-cert-card placeholder">
              <Award size={28} />
              <span>{t('aboutPage.hero.badges.warranty', 'Lifetime warranty')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="about-team-wrap">
        <TeamSection />
      </section>

      {/* CTA */}
      <section className="about-cta-section">
        <div className="container-premium">
          <div className="about-cta-card">
            <div>
              <span className="section-eyebrow light">
                {t('aboutPage.hero.eyebrow', 'About Crystal Dental')}
              </span>
              <h2>{t('aboutPage.cta.title', 'Ready to start your journey?')}</h2>
              <p>
                {t(
                  'aboutPage.cta.subtitle',
                  "Send us a few photos and we'll come back with a personalised treatment plan within 24 hours."
                )}
              </p>
            </div>
            <div className="about-cta-actions">
              <Link to="/appointment" className="btn-crystal">
                <CalendarCheck size={16} />
                {t('aboutPage.cta.primary', 'Book Appointment')}
              </Link>
              <Link to="/contact" className="btn-glass">
                <MessageCircle size={16} />
                {t('aboutPage.cta.secondary', 'Contact us')}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
